import type { IAuthRepository, SendOtpResult, VerifyOtpResult, OnboardingResult, WhatsAppLinkCodeResult } from '@src/domain/users/repository';
import type { User } from '@src/domain/users/entities/user.entity';
import { AuthTokens } from '@src/domain/users/value_objects';
import { UserMapper } from '../mappers/user_mapper';
import type {
    ApiUserResponse,
    ApiRefreshTokenResponse,
    ApiSendOtpResponse,
    ApiVerifyOtpResponse,
    ApiOnboardingResponse,
    ApiWhatsAppLinkCodeResponse,
} from './api-types';
import type { ApiClient } from '../api/client';
import { authTokens } from '../api/client';

/**
 * Auth Repository Implementation
 * Handles authentication operations via API
 * 
 * Infrastructure layer - only imports from domain layer and its own internal types.
 * Returns domain entities and value objects only.
 */
export class AuthRepositoryImpl implements IAuthRepository {
    constructor(private readonly apiClient: ApiClient) { }

    async sendOtp(email: string): Promise<SendOtpResult> {
        const response = await this.apiClient.post<ApiSendOtpResponse>(
            '/auth/send-otp',
            { email }
        );

        return {
            cooldownSeconds: response.cooldown_seconds,
            email: response.email,
            expiresInMinutes: response.expires_in_minutes,
            message: response.message,
        };
    }

    async verifyOtp(email: string, code: string): Promise<VerifyOtpResult> {
        const response = await this.apiClient.post<ApiVerifyOtpResponse>(
            '/auth/verify-otp',
            { email, code }
        );

        // Tokens are automatically stored by the API client's response interceptor

        return {
            email: response.email,
            isNewUser: response.is_new_user,
            profileState: response.profile_state,
            tokens: AuthTokens.create({
                accessToken: response.tokens.access_token,
                refreshToken: response.tokens.refresh_token,
                tokenType: response.tokens.token_type,
            }),
            userId: response.user_id,
            whatsappLinked: response.whatsapp_linked,
        };
    }

    async setUniversity(universityId: string): Promise<OnboardingResult> {
        const response = await this.apiClient.post<ApiOnboardingResponse>(
            '/auth/onboarding/university',
            { university_id: universityId }
        );

        const user = UserMapper.toDomain({
            user_id: response.user_id,
            email: response.email,
            first_name: response.first_name,
            middle_name: null,
            last_name: response.last_name,
            display_name: `${response.first_name} ${response.last_name}`.trim() || response.email,
            full_name: `${response.first_name} ${response.last_name}`.trim(),
            profile_picture_url: response.profile_picture_url,
            phone_number: response.phone_number || '',
            is_active: true,
            is_verified: response.is_verified,
            is_banned: false,
            created_at: response.created_at,
            updated_at: response.updated_at,
            university_id: response.university_id,
            university_name: response.university_name,
            profile_state: response.profile_state,
            whatsapp_linked: response.whatsapp_linked,
        });

        return { user };
    }

    async setName(firstName: string, lastName: string, middleName?: string): Promise<OnboardingResult> {
        const response = await this.apiClient.post<ApiOnboardingResponse>(
            '/auth/onboarding/name',
            {
                first_name: firstName,
                last_name: lastName,
                middle_name: middleName,
            }
        );

        const user = UserMapper.toDomain({
            user_id: response.user_id,
            email: response.email,
            first_name: response.first_name,
            middle_name: null,
            last_name: response.last_name,
            display_name: `${response.first_name} ${response.last_name}`.trim(),
            full_name: `${response.first_name} ${response.last_name}`.trim(),
            profile_picture_url: response.profile_picture_url,
            phone_number: response.phone_number || '',
            is_active: true,
            is_verified: response.is_verified,
            is_banned: false,
            created_at: response.created_at,
            updated_at: response.updated_at,
            university_id: response.university_id,
            university_name: response.university_name,
            profile_state: response.profile_state,
            whatsapp_linked: response.whatsapp_linked,
        });

        return { user };
    }

    async refreshToken(refreshToken: string): Promise<AuthTokens> {
        const response = await this.apiClient.post<ApiRefreshTokenResponse>(
            '/auth/refresh',
            { refresh_token: refreshToken }
        );

        return AuthTokens.create({
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
            tokenType: response.token_type,
        });
    }

    async getCurrentUser(): Promise<User> {
        const response = await this.apiClient.get<ApiUserResponse>('/auth/me');
        return UserMapper.toDomain(response);
    }

    async logout(): Promise<void> {
        // Clear tokens from storage
        authTokens.clear();
    }

    async generateWhatsAppLinkCode(): Promise<WhatsAppLinkCodeResult> {
        const response = await this.apiClient.post<ApiWhatsAppLinkCodeResponse>(
            '/auth/whatsapp/generate-code',
            {}
        );

        return {
            linkCode: response.link_code,
            expiresInSeconds: response.expires_in_seconds,
            message: response.message,
        };
    }
}
